/**
 * Google SSO — Master Admin configuration, rendered as a tab on the
 * Settings page.
 *
 *  Reads / writes the encrypted `google_sso` PlatformSetting row. The
 *  Client Secret is NEVER returned by the API in plaintext — reads only
 *  surface a `clientSecretSet` boolean. The "Save" call only sends
 *  `clientSecret` when the admin has actually entered a new one, so the
 *  encrypted blob stored at rest is preserved when the admin just flips
 *  Enabled or edits the Client ID.
 */
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { SettingsUpdatedAt } from './SettingsUpdatedAt'
import {
  ShieldCheck,
  Eye,
  EyeOff,
  Copy,
  Check,
  Loader2,
  Save,
} from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import { googleSsoAdminConfig } from '../lib/types'
import { copyText } from '../lib/clipboard'

// Flat button styles matching the Save buttons on the other Settings tabs.
const btnPrimary =
  'shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium ' +
  'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] ' +
  'cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed'
const btnSecondary =
  'shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium ' +
  'bg-[color:var(--color-card)] text-[color:var(--color-ink)] border border-[color:var(--color-rule-strong)] ' +
  'hover:bg-[color:var(--color-card-hover)] hover:border-[color:var(--color-ink-muted)] ' +
  'cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed'

export function GoogleSsoSettings() {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    enabled: false,
    clientId: '',
    clientSecret: '', // empty string means "don't change" on save
  })
  const [showSecret, setShowSecret] = useState(false)
  const [copiedValue, setCopiedValue] = useState(null)

  const configQuery = useQuery({
    queryKey: ['platform', 'google-sso'],
    queryFn: async () => {
      const res = await api.get('/super-admin/google-sso')
      return googleSsoAdminConfig.parse(res.data)
    },
  })

  // The backend callback Google redirects to — derived server-side from
  // API_URL and returned by the config endpoint, so it shows the real
  // backend URL (e.g. https://api.taskmagix.com/api/v1/auth/google/callback)
  // whatever domain this Settings page is open on.
  const redirectUri = configQuery.data?.redirectUri ?? ''
  const copied = copiedValue !== null && copiedValue === redirectUri

  // Hydrate the form from the server on first load and after each save.
  // Client Secret stays empty — the user only re-enters it to rotate.
  useEffect(() => {
    const data = configQuery.data
    if (!data) return
    setForm({
      enabled: Boolean(data.enabled),
      clientId: data.clientId || '',
      clientSecret: '',
    })
  }, [configQuery.data])

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const body = {
        enabled: payload.enabled,
        clientId: payload.clientId,
        // Only include clientSecret when the user actually entered one, so
        // the stored encrypted blob isn't cleared on every save.
        ...(payload.clientSecret ? { clientSecret: payload.clientSecret } : {}),
      }
      const res = await api.put('/super-admin/google-sso', body)
      return googleSsoAdminConfig.parse(res.data)
    },
    onSuccess: () => {
      toast.success('Google SSO settings saved successfully.')
      void qc.invalidateQueries({ queryKey: ['platform', 'google-sso'] })
    },
    onError: (err) => {
      const { message, fields } = extractApiError(err)
      if (fields) {
        for (const msg of Object.values(fields)) toast.error(msg)
      } else {
        toast.error(message || 'Could not save settings.')
      }
    },
  })

  async function copyRedirectUri() {
    if (!redirectUri) return
    // Routed through the shared helper so a refused/unavailable Clipboard API
    // falls back to a gesture-based execCommand copy, and the label stays on
    // "Copied" rather than reverting (product decision — a confirmation that
    // erases itself left users unsure the copy had happened). See lib/clipboard.js.
    if (!(await copyText(redirectUri))) {
      toast.error('Could not copy. Please copy it manually.')
      return
    }
    setCopiedValue(redirectUri)
  }

  const isLoading = configQuery.isLoading
  const isError = configQuery.isError
  const secretSet = configQuery.data?.clientSecretSet === true
  const dirty =
    form.enabled !== Boolean(configQuery.data?.enabled) ||
    form.clientId !== (configQuery.data?.clientId ?? '') ||
    form.clientSecret.length > 0

  return (
    <div>
      {/* Tab header — matches the other Settings tabs: icon + heading +
       *  one-line description on the left, status + actions on the right. */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-xl text-[color:var(--color-ink-strong)] flex items-center gap-2.5 m-0">
            <GoogleGlyph size={20} />
            Google SSO
          </h2>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Configure Google OAuth credentials and toggle SSO on or off for
            the entire platform.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusBadge
            enabled={form.enabled}
            configured={
              Boolean(form.clientId) && (secretSet || form.clientSecret)
            }
          />
          <button
            type="button"
            onClick={() => {
              const data = configQuery.data
              setForm({
                enabled: Boolean(data?.enabled),
                clientId: data?.clientId || '',
                clientSecret: '',
              })
            }}
            disabled={!dirty || saveMutation.isPending}
            className={btnSecondary}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate(form)}
            disabled={!dirty || saveMutation.isPending}
            className={btnPrimary}
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <Save size={14} />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] py-8">
          Loading…
        </p>
      ) : isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)] py-8">
          Could not load Google SSO settings.
        </p>
      ) : (
        <>
          {/* Single card — enable toggle on top, OAuth credentials below,
           *  separated by a divider. The redundant section blurbs were
           *  dropped; the Console link now sits on the Client ID row. */}
          <div className="bg-[color:var(--color-card)] rounded-2xl border border-[color:var(--color-rule)] p-6">
            <div className="flex items-center justify-between gap-6">
              <h3 className="font-semibold text-[color:var(--color-ink-strong)] text-[15px]">
                Enable Google SSO
              </h3>
              <Toggle
                checked={form.enabled}
                onChange={(v) => setForm((f) => ({ ...f, enabled: v }))}
              />
            </div>

            <div className="my-5 border-t border-[color:var(--color-rule)]" />

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <label className="block text-[13px] font-semibold text-[color:var(--color-ink-strong)]">
                    Google Client ID
                    {form.enabled ? (
                      <span className="ml-0.5 text-[color:var(--color-priority-urgent)]">*</span>
                    ) : null}
                  </label>
                  <a
                    href="https://console.cloud.google.com/apis/credentials"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[color:var(--color-accent-strong)] font-semibold whitespace-nowrap"
                  >
                    Open Google Console →
                  </a>
                </div>
                <input
                  type="text"
                  value={form.clientId}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, clientId: e.target.value }))
                  }
                  placeholder="xxx-xxxxx.apps.googleusercontent.com"
                  className="w-full h-11 px-3 border border-[color:var(--color-rule)] rounded-lg bg-[color:var(--color-card)] text-sm focus:outline-none focus:border-[color:var(--color-accent-strong)] focus:ring-2 focus:ring-[color:var(--color-accent-bg)]"
                />
              </div>

              <div>
                <label className="block text-[13px] font-semibold text-[color:var(--color-ink-strong)] mb-1.5">
                  Google Client Secret
                  {form.enabled && !secretSet ? (
                    <span className="ml-0.5 text-[color:var(--color-priority-urgent)]">*</span>
                  ) : null}
                </label>
                <div className="relative">
                  <input
                    type={showSecret ? 'text' : 'password'}
                    value={form.clientSecret}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, clientSecret: e.target.value }))
                    }
                    placeholder={
                      secretSet ? '•••••••••• (leave blank to keep current)' : 'Paste Client Secret'
                    }
                    autoComplete="off"
                    className="w-full h-11 pl-3 pr-10 border border-[color:var(--color-rule)] rounded-lg bg-[color:var(--color-card)] text-sm focus:outline-none focus:border-[color:var(--color-accent-strong)] focus:ring-2 focus:ring-[color:var(--color-accent-bg)]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret((v) => !v)}
                    aria-label={showSecret ? 'Hide Secret' : 'Show Secret'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)]"
                  >
                    {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-[11px] text-[color:var(--color-ink-subtle)] mt-1.5 flex items-center gap-1.5">
                  <ShieldCheck size={12} className="shrink-0" />
                  Encrypted at rest (AES-256-GCM) — never returned in API responses or logs.
                  {secretSet ? ' Secret on file.' : ' No secret saved yet.'}
                </p>
              </div>

              <div>
                <label className="block text-[13px] font-semibold text-[color:var(--color-ink-strong)] mb-1.5">
                  Authorised redirect URI
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={redirectUri}
                    readOnly
                    className="flex-1 h-11 px-3 border border-[color:var(--color-rule)] rounded-lg bg-[color:var(--color-canvas)] text-sm font-mono text-[color:var(--color-ink-muted)] focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={copyRedirectUri}
                    className="px-3 h-11 border border-[color:var(--color-rule)] rounded-lg bg-[color:var(--color-card)] text-sm font-medium hover:bg-[color:var(--color-card-hover)] inline-flex items-center gap-1.5"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <p className="text-[11px] text-[color:var(--color-ink-subtle)] mt-1.5">
                  Paste this into &quot;Authorised redirect URIs&quot; in Google Cloud Console for the OAuth client. It points at the API, derived from your API_URL setting.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
      <SettingsUpdatedAt at={configQuery.data?.updatedAt} />
    </div>
  )
}

function Toggle({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ' +
        (checked ? 'bg-[color:var(--color-success)]' : 'bg-[color:var(--color-rule)]')
      }
    >
      <span
        className={
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ' +
          (checked ? 'translate-x-5' : 'translate-x-0')
        }
      />
    </button>
  )
}

function StatusBadge({ enabled, configured }) {
  if (!enabled) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] text-xs font-semibold text-[color:var(--color-ink-muted)]">
        <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--color-ink-muted)]" />
        Disabled
      </span>
    )
  }
  if (!configured) {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-700 dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        Needs credentials
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-semibold text-emerald-700 dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
      Enabled
    </span>
  )
}

function GoogleGlyph({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" aria-hidden="true">
      <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  )
}
